import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";

import { useLazyGetSurveysQuery } from "@/redux/service";
import { SurveyViewFormGeneralInfoProps } from "../../types";
import ImageCard from "@/components_v2/Card/ImageCard";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import {
  HookFormInfiniteScrollDropdown,
  HookFormInput,
  HookFormTextArea,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  SurveyViewDataFormat,
  SurveyViewDistributionFormat,
  SurveyViewPublicationDestination,
} from "@/redux/survey-views/types";

import "./styles.css";

const SurveyViewFormGeneralInfo = ({
  form,
}: SurveyViewFormGeneralInfoProps) => {
  const { t } = useTranslation();
  const [getSurveys] = useLazyGetSurveysQuery();

  const config = useWatch({
    control: form.control,
    name: "config",
  });

  return (
    <div className="survey-view-content">
      <h2 className="header-s-medium">
        {t("surveyViews.labels.formGeneralInformationLabel")}
      </h2>
      <div className="survey-view-general-info-form">
        <FormFieldWrapper label={t("surveyViews.labels.survey")} required>
          <HookFormInfiniteScrollDropdown
            name="survey"
            control={form.control}
            fetchFn={getSurveys}
            disabled={
              Boolean(config.publicationDestination) &&
              config.publicationDestination !==
                SurveyViewPublicationDestination.EXTERNAL
            }
            labelFieldName="title"
            valueFieldName="id"
            searchFieldName="title"
            placeholder={t("surveyViews.placeholders.selectSurvey")}
          />
        </FormFieldWrapper>

        <FormFieldWrapper label={t("surveyViews.labels.title")} required>
          <HookFormInput
            name="title"
            placeholder={t("surveyViews.placeholders.titleSurveyView")}
            control={form.control}
            disabled={
              Boolean(config.publicationDestination) &&
              config.publicationDestination !==
                SurveyViewPublicationDestination.EXTERNAL
            }
          />
        </FormFieldWrapper>

        <FormFieldWrapper label={t("surveyViews.labels.description")}>
          <HookFormTextArea
            name="description"
            placeholder={t("surveyViews.placeholders.descriptionSurveyView")}
            control={form.control}
          />
        </FormFieldWrapper>

        <FormFieldWrapper
          label={t("surveyViews.labels.dataFormat")}
          error={{
            message: form.formState.errors.config?.dataFormat?.message,
          }}
          required
        >
          <div className="survey-view-general-info-cards-container">
            <ImageCard
              title={t("surveyViews.labels.raw")}
              description={t("surveyViews.labels.rawDescription")}
              image="/images/q&a_format.png"
              onClick={() =>
                form.setValue("config.dataFormat", SurveyViewDataFormat.RAW)
              }
              isSelected={config.dataFormat === SurveyViewDataFormat.RAW}
            />

            <ImageCard
              title={t("surveyViews.labels.descriptive")}
              description={t("surveyViews.labels.descriptiveDescription")}
              image="/images/integrated_response.png"
              onClick={() =>
                form.setValue(
                  "config.dataFormat",
                  SurveyViewDataFormat.DESCRIPTIVE,
                )
              }
              isSelected={
                config.dataFormat === SurveyViewDataFormat.DESCRIPTIVE
              }
            />
          </div>
        </FormFieldWrapper>

        <FormFieldWrapper
          label={t("surveyViews.labels.distributionFormat")}
          error={{
            message: form.formState.errors.config?.distributionFormat?.message,
          }}
          required
        >
          <div className="survey-view-general-info-cards-container">
            <ImageCard
              title={t("surveyViews.labels.digital")}
              description={t("surveyViews.labels.digitalDescription")}
              image="/images/online_sharing.png"
              onClick={() =>
                form.setValue(
                  "config.distributionFormat",
                  SurveyViewDistributionFormat.DIGITAL,
                )
              }
              isSelected={
                config.distributionFormat ===
                SurveyViewDistributionFormat.DIGITAL
              }
            />

            <ImageCard
              title={t("surveyViews.labels.print")}
              description={t("surveyViews.labels.printDescription")}
              image="/images/printing.png"
              onClick={() =>
                form.setValue(
                  "config.distributionFormat",
                  SurveyViewDistributionFormat.PRINT,
                )
              }
              isSelected={
                config.distributionFormat === SurveyViewDistributionFormat.PRINT
              }
            />
          </div>
        </FormFieldWrapper>
      </div>
    </div>
  );
};

export default SurveyViewFormGeneralInfo;
